import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon, PlusCircle, X, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';

export default function PitchCreatePage() {
  const { user } = useAuth();
  const { createPitch } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('basics');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    // Core
    companyName: '',
    tagline: '',
    problem: '',
    solution: '',
    marketSize: '',
    businessModel: '',
    team: '',
    
    // Traction
    traction: {
      customerInterviews: 0,
      waitlistSignups: 0,
      lettersOfIntent: 0,
      pilotCustomers: 0,
      mvpStatus: 'none' as const,
      mvpUrl: '',
      monthlyActiveUsers: 0,
      revenueRunRate: 0,
    },
    
    // Competition
    competitive: {
      directCompetitors: [] as string[],
      indirectCompetitors: [] as string[],
      competitiveAdvantage: '',
      barriersToEntry: [] as string[],
      substituteProducts: [] as string[],
    },
    
    // Financial
    financial: {
      fundingAsk: 0,
      useOfFunds: {
        product: 0,
        marketing: 0,
        team: 0,
        operations: 0,
        legal: 0,
        other: 0,
      },
      runwayMonths: 0,
      burnRate: 0,
      revenueProjection: {
        month1: 0,
        month3: 0,
        month6: 0,
        month12: 0,
      },
      breakevenMonth: 0,
    },
    
    // GTM
    gtm: {
      customerChannels: [] as string[],
      salesStrategy: '',
      marketingPlan: '',
      partnerships: [] as string[],
      targetEarlyAdopters: '',
    },
    
    // IP
    ip: {
      patentStatus: 'none' as const,
      patentNumbers: [] as string[],
      proprietaryTechnology: '',
      technicalArchitecture: '',
      developmentStage: 'idea' as const,
      techStack: [] as string[],
    },
    
    // Risks
    risks: {
      regulatoryHurdles: [] as string[],
      complianceRequirements: [] as string[],
      keyRisks: [] as Array<{ risk: string; impact: 'low' | 'medium' | 'high'; mitigation: string }>,
      marketRisks: [] as string[],
      executionRisks: [] as string[],
    },
  });

  const [newCompetitor, setNewCompetitor] = useState('');
  const [newBarrier, setNewBarrier] = useState('');
  const [newChannel, setNewChannel] = useState('');
  const [newPatent, setNewPatent] = useState('');
  const [newTech, setNewTech] = useState('');
  const [newRegulatory, setNewRegulatory] = useState('');
  const [newCompliance, setNewCompliance] = useState('');
  const [newMarketRisk, setNewMarketRisk] = useState('');
  const [newExecutionRisk, setNewExecutionRisk] = useState('');
  const [newRisk, setNewRisk] = useState({ risk: '', impact: 'medium' as const, mitigation: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setIsSubmitting(true);
    
    try {
      const pitchData = {
        ...form,
        founderId: user.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      const id = await createPitch(pitchData);
      
      toast({
        title: "Pitch Created!",
        description: "Your pitch has been submitted for analysis.",
      });
      
      navigate(`/pitch/${id}`);
    } catch (error) {
      console.error('Failed to create pitch:', error);
      toast({
        title: "Error",
        description: "Failed to create pitch. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateNested = (section: string, field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
  };

  const addArrayItem = (section: string, field: string, value: string) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: [...(prev[section as keyof typeof prev] as any)[field], value.trim()]
      }
    }));
  };

  const removeArrayItem = (section: string, field: string, index: number) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: (prev[section as keyof typeof prev] as any)[field].filter((_: any, i: number) => i !== index)
      }
    }));
  };

  const addRisk = () => {
    if (!newRisk.risk.trim() || !newRisk.mitigation.trim()) return;
    setForm(prev => ({
      ...prev,
      risks: {
        ...prev.risks,
        keyRisks: [...prev.risks.keyRisks, { ...newRisk }]
      }
    }));
    setNewRisk({ risk: '', impact: 'medium', mitigation: '' });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">Create New Pitch</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Complete all sections thoroughly. Our AI will analyze every aspect brutally honestly.
          </p>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <InfoIcon className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              Pre-seed tips: Be specific, include numbers, and demonstrate validation. The more detail you provide, the better your analysis will be.
            </AlertDescription>
          </Alert>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-4 lg:grid-cols-8 w-full">
              <TabsTrigger value="basics">Basics</TabsTrigger>
              <TabsTrigger value="traction">Traction</TabsTrigger>
              <TabsTrigger value="competition">Competition</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="gtm">Go-to-Market</TabsTrigger>
              <TabsTrigger value="ip">IP & Tech</TabsTrigger>
              <TabsTrigger value="risks">Risks</TabsTrigger>
              <TabsTrigger value="review">Review</TabsTrigger>
            </TabsList>

            {/* Tab 1: Basics */}
            <TabsContent value="basics" className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">Company Name *</Label>
                <Input
                  value={form.companyName}
                  onChange={e => setForm({ ...form, companyName: e.target.value })}
                  placeholder="Enter your company name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Tagline *</Label>
                <Input
                  value={form.tagline}
                  onChange={e => setForm({ ...form, tagline: e.target.value })}
                  placeholder="One-line description of your venture"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Problem *</Label>
                <Textarea
                  value={form.problem}
                  onChange={e => setForm({ ...form, problem: e.target.value })}
                  placeholder="Describe the problem you are solving. Who has it? How painful is it? What's the current solution?"
                  className="min-h-[200px] resize-y"
                  required
                />
                <p className="text-xs text-muted-foreground">💡 Be specific. Include customer quotes, anecdotes, or data points.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Solution *</Label>
                <Textarea
                  value={form.solution}
                  onChange={e => setForm({ ...form, solution: e.target.value })}
                  placeholder="Describe your solution. How does it solve the problem? What makes it unique?"
                  className="min-h-[200px] resize-y"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Market Size *</Label>
                <Textarea
                  value={form.marketSize}
                  onChange={e => setForm({ ...form, marketSize: e.target.value })}
                  placeholder="Total Addressable Market (TAM), Serviceable Available Market (SAM), Serviceable Obtainable Market (SOM)"
                  className="min-h-[150px] resize-y"
                  required
                />
                <p className="text-xs text-muted-foreground">💡 Include numbers and sources. e.g., "TAM: $5B, growing at 15% annually (Source: Gartner 2024)"</p>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Business Model *</Label>
                <Textarea
                  value={form.businessModel}
                  onChange={e => setForm({ ...form, businessModel: e.target.value })}
                  placeholder="How will you make money? Subscription? Marketplace? Transaction fees? Enterprise SaaS?"
                  className="min-h-[150px] resize-y"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Team *</Label>
                <Textarea
                  value={form.team}
                  onChange={e => setForm({ ...form, team: e.target.value })}
                  placeholder="Key team members, their backgrounds, relevant experience, past successes"
                  className="min-h-[150px] resize-y"
                  required
                />
                <p className="text-xs text-muted-foreground">💡 Include LinkedIn profiles, previous exits, domain expertise</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setActiveTab('traction')}>Next: Traction →</Button>
              </div>
            </TabsContent>

            {/* Tab 2: Traction */}
            <TabsContent value="traction" className="space-y-6">
              <h3 className="text-lg font-semibold">Traction & Validation</h3>
              <p className="text-sm text-muted-foreground">Show us what you've already accomplished</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer Interviews Conducted</Label>
                  <Input
                    type="number"
                    value={form.traction.customerInterviews}
                    onChange={e => updateNested('traction', 'customerInterviews', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Waitlist Signups</Label>
                  <Input
                    type="number"
                    value={form.traction.waitlistSignups}
                    onChange={e => updateNested('traction', 'waitlistSignups', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Letters of Intent (LOIs)</Label>
                  <Input
                    type="number"
                    value={form.traction.lettersOfIntent}
                    onChange={e => updateNested('traction', 'lettersOfIntent', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Pilot Customers</Label>
                  <Input
                    type="number"
                    value={form.traction.pilotCustomers}
                    onChange={e => updateNested('traction', 'pilotCustomers', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>MVP Status</Label>
                <Select 
                  value={form.traction.mvpStatus}
                  onValueChange={(value: any) => updateNested('traction', 'mvpStatus', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select MVP status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No MVP yet</SelectItem>
                    <SelectItem value="in_development">In Development</SelectItem>
                    <SelectItem value="beta">Beta Testing</SelectItem>
                    <SelectItem value="live">Live / Launched</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.traction.mvpStatus !== 'none' && (
                <div className="space-y-2">
                  <Label>MVP URL (if applicable)</Label>
                  <Input
                    value={form.traction.mvpUrl}
                    onChange={e => updateNested('traction', 'mvpUrl', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monthly Active Users (if applicable)</Label>
                  <Input
                    type="number"
                    value={form.traction.monthlyActiveUsers}
                    onChange={e => updateNested('traction', 'monthlyActiveUsers', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Monthly Revenue Run Rate ($)</Label>
                  <Input
                    type="number"
                    value={form.traction.revenueRunRate}
                    onChange={e => updateNested('traction', 'revenueRunRate', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('basics')}>← Previous</Button>
                <Button onClick={() => setActiveTab('competition')}>Next: Competition →</Button>
              </div>
            </TabsContent>

            {/* Tab 3: Competition */}
            <TabsContent value="competition" className="space-y-6">
              <h3 className="text-lg font-semibold">Competitive Landscape</h3>

              <div className="space-y-2">
                <Label>Direct Competitors</Label>
                <div className="flex gap-2">
                  <Input
                    value={newCompetitor}
                    onChange={e => setNewCompetitor(e.target.value)}
                    placeholder="Add a competitor"
                  />
                  <Button 
                    type="button" 
                    onClick={() => {
                      addArrayItem('competitive', 'directCompetitors', newCompetitor);
                      setNewCompetitor('');
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.competitive.directCompetitors.map((comp, i) => (
                    <div key={i} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md">
                      <span className="text-sm">{comp}</span>
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeArrayItem('competitive', 'directCompetitors', i)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Competitive Advantage</Label>
                <Textarea
                  value={form.competitive.competitiveAdvantage}
                  onChange={e => updateNested('competitive', 'competitiveAdvantage', e.target.value)}
                  placeholder="What makes you different and defensible? Technology? Network effects? Brand? Data?"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Barriers to Entry</Label>
                <div className="flex gap-2">
                  <Input
                    value={newBarrier}
                    onChange={e => setNewBarrier(e.target.value)}
                    placeholder="Add a barrier"
                  />
                  <Button 
                    type="button" 
                    onClick={() => {
                      addArrayItem('competitive', 'barriersToEntry', newBarrier);
                      setNewBarrier('');
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.competitive.barriersToEntry.map((barrier, i) => (
                    <div key={i} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md">
                      <span className="text-sm">{barrier}</span>
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeArrayItem('competitive', 'barriersToEntry', i)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('traction')}>← Previous</Button>
                <Button onClick={() => setActiveTab('financial')}>Next: Financial →</Button>
              </div>
            </TabsContent>

            {/* Tab 4: Financial */}
            <TabsContent value="financial" className="space-y-6">
              <h3 className="text-lg font-semibold">Financial Projections</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Funding Ask ($)</Label>
                  <Input
                    type="number"
                    value={form.financial.fundingAsk}
                    onChange={e => updateNested('financial', 'fundingAsk', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Runway (months)</Label>
                  <Input
                    type="number"
                    value={form.financial.runwayMonths}
                    onChange={e => updateNested('financial', 'runwayMonths', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Monthly Burn Rate ($)</Label>
                  <Input
                    type="number"
                    value={form.financial.burnRate}
                    onChange={e => updateNested('financial', 'burnRate', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Breakeven Month (from now)</Label>
                  <Input
                    type="number"
                    value={form.financial.breakevenMonth}
                    onChange={e => updateNested('financial', 'breakevenMonth', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Label>Use of Funds (%)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Product Development</Label>
                    <Input
                      type="number"
                      value={form.financial.useOfFunds.product}
                      onChange={e => updateNested('financial', 'useOfFunds', { 
                        ...form.financial.useOfFunds, 
                        product: parseInt(e.target.value) || 0 
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Marketing & Sales</Label>
                    <Input
                      type="number"
                      value={form.financial.useOfFunds.marketing}
                      onChange={e => updateNested('financial', 'useOfFunds', { 
                        ...form.financial.useOfFunds, 
                        marketing: parseInt(e.target.value) || 0 
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Team & Hiring</Label>
                    <Input
                      type="number"
                      value={form.financial.useOfFunds.team}
                      onChange={e => updateNested('financial', 'useOfFunds', { 
                        ...form.financial.useOfFunds, 
                        team: parseInt(e.target.value) || 0 
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Operations</Label>
                    <Input
                      type="number"
                      value={form.financial.useOfFunds.operations}
                      onChange={e => updateNested('financial', 'useOfFunds', { 
                        ...form.financial.useOfFunds, 
                        operations: parseInt(e.target.value) || 0 
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Legal & Admin</Label>
                    <Input
                      type="number"
                      value={form.financial.useOfFunds.legal}
                      onChange={e => updateNested('financial', 'useOfFunds', { 
                        ...form.financial.useOfFunds, 
                        legal: parseInt(e.target.value) || 0 
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Other</Label>
                    <Input
                      type="number"
                      value={form.financial.useOfFunds.other}
                      onChange={e => updateNested('financial', 'useOfFunds', { 
                        ...form.financial.useOfFunds, 
                        other: parseInt(e.target.value) || 0 
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Total: {Object.values(form.financial.useOfFunds).reduce((a, b) => a + b, 0)}% 
                  {Object.values(form.financial.useOfFunds).reduce((a, b) => a + b, 0) !== 100 && 
                    " (should total 100%)"}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Revenue Projections ($)</Label>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Month 1</Label>
                    <Input
                      type="number"
                      value={form.financial.revenueProjection.month1}
                      onChange={e => updateNested('financial', 'revenueProjection', {
                        ...form.financial.revenueProjection,
                        month1: parseInt(e.target.value) || 0
                      })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Month 3</Label>
                    <Input
                      type="number"
                      value={form.financial.revenueProjection.month3}
                      onChange={e => updateNested('financial', 'revenueProjection', {
                        ...form.financial.revenueProjection,
                        month3: parseInt(e.target.value) || 0
                      })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Month 6</Label>
                    <Input
                      type="number"
                      value={form.financial.revenueProjection.month6}
                      onChange={e => updateNested('financial', 'revenueProjection', {
                        ...form.financial.revenueProjection,
                        month6: parseInt(e.target.value) || 0
                      })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Month 12</Label>
                    <Input
                      type="number"
                      value={form.financial.revenueProjection.month12}
                      onChange={e => updateNested('financial', 'revenueProjection', {
                        ...form.financial.revenueProjection,
                        month12: parseInt(e.target.value) || 0
                      })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('competition')}>← Previous</Button>
                <Button onClick={() => setActiveTab('gtm')}>Next: Go-to-Market →</Button>
              </div>
            </TabsContent>

            {/* Tab 5: Go-to-Market */}
            <TabsContent value="gtm" className="space-y-6">
              <h3 className="text-lg font-semibold">Go-to-Market Strategy</h3>

              <div className="space-y-2">
                <Label>Customer Acquisition Channels</Label>
                <div className="flex gap-2">
                  <Input
                    value={newChannel}
                    onChange={e => setNewChannel(e.target.value)}
                    placeholder="e.g., Content Marketing, Paid Ads, Sales Team"
                  />
                  <Button 
                    type="button" 
                    onClick={() => {
                      addArrayItem('gtm', 'customerChannels', newChannel);
                      setNewChannel('');
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.gtm.customerChannels.map((channel, i) => (
                    <div key={i} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md">
                      <span className="text-sm">{channel}</span>
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeArrayItem('gtm', 'customerChannels', i)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sales Strategy</Label>
                <Textarea
                  value={form.gtm.salesStrategy}
                  onChange={e => updateNested('gtm', 'salesStrategy', e.target.value)}
                  placeholder="Self-serve? Enterprise sales? Channel partners?"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Marketing Plan</Label>
                <Textarea
                  value={form.gtm.marketingPlan}
                  onChange={e => updateNested('gtm', 'marketingPlan', e.target.value)}
                  placeholder="Content marketing, SEO, PR, Events, etc."
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Target Early Adopters</Label>
                <Textarea
                  value={form.gtm.targetEarlyAdopters}
                  onChange={e => updateNested('gtm', 'targetEarlyAdopters', e.target.value)}
                  placeholder="Who will you target first? Why them?"
                  className="min-h-[100px]"
                />
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('financial')}>← Previous</Button>
                <Button onClick={() => setActiveTab('ip')}>Next: IP & Tech →</Button>
              </div>
            </TabsContent>

            {/* Tab 6: IP & Technology */}
            <TabsContent value="ip" className="space-y-6">
              <h3 className="text-lg font-semibold">IP & Technology</h3>

              <div className="space-y-2">
                <Label>Patent Status</Label>
                <Select 
                  value={form.ip.patentStatus}
                  onValueChange={(value: any) => updateNested('ip', 'patentStatus', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select patent status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No patents</SelectItem>
                    <SelectItem value="filed">Filed</SelectItem>
                    <SelectItem value="provisional">Provisional</SelectItem>
                    <SelectItem value="granted">Granted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.ip.patentStatus !== 'none' && (
                <div className="space-y-2">
                  <Label>Patent Numbers</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newPatent}
                      onChange={e => setNewPatent(e.target.value)}
                      placeholder="Add patent number"
                    />
                    <Button 
                      type="button" 
                      onClick={() => {
                        addArrayItem('ip', 'patentNumbers', newPatent);
                        setNewPatent('');
                      }}
                    >
                      <PlusCircle className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.ip.patentNumbers.map((patent, i) => (
                      <div key={i} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md">
                        <span className="text-sm">{patent}</span>
                        <X 
                          className="h-3 w-3 cursor-pointer" 
                          onClick={() => removeArrayItem('ip', 'patentNumbers', i)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Proprietary Technology</Label>
                <Textarea
                  value={form.ip.proprietaryTechnology}
                  onChange={e => updateNested('ip', 'proprietaryTechnology', e.target.value)}
                  placeholder="What technology do you own or have exclusive rights to?"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Technical Architecture</Label>
                <Textarea
                  value={form.ip.technicalArchitecture}
                  onChange={e => updateNested('ip', 'technicalArchitecture', e.target.value)}
                  placeholder="High-level technical architecture"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Development Stage</Label>
                <Select 
                  value={form.ip.developmentStage}
                  onValueChange={(value: any) => updateNested('ip', 'developmentStage', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select development stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="idea">Idea / Concept</SelectItem>
                    <SelectItem value="prototype">Prototype</SelectItem>
                    <SelectItem value="alpha">Alpha</SelectItem>
                    <SelectItem value="beta">Beta</SelectItem>
                    <SelectItem value="live">Live / Launched</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tech Stack</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTech}
                    onChange={e => setNewTech(e.target.value)}
                    placeholder="e.g., React, Python, AWS"
                  />
                  <Button 
                    type="button" 
                    onClick={() => {
                      addArrayItem('ip', 'techStack', newTech);
                      setNewTech('');
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.ip.techStack.map((tech, i) => (
                    <div key={i} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md">
                      <span className="text-sm">{tech}</span>
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeArrayItem('ip', 'techStack', i)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('gtm')}>← Previous</Button>
                <Button onClick={() => setActiveTab('risks')}>Next: Risks →</Button>
              </div>
            </TabsContent>

            {/* Tab 7: Risks */}
            <TabsContent value="risks" className="space-y-6">
              <h3 className="text-lg font-semibold">Risks & Mitigations</h3>

              <div className="space-y-2">
                <Label>Regulatory Hurdles</Label>
                <div className="flex gap-2">
                  <Input
                    value={newRegulatory}
                    onChange={e => setNewRegulatory(e.target.value)}
                    placeholder="Add regulatory hurdle"
                  />
                  <Button 
                    type="button" 
                    onClick={() => {
                      addArrayItem('risks', 'regulatoryHurdles', newRegulatory);
                      setNewRegulatory('');
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.risks.regulatoryHurdles.map((risk, i) => (
                    <div key={i} className="flex items-center gap-1 bg-secondary px-2 py-1 rounded-md">
                      <span className="text-sm">{risk}</span>
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeArrayItem('risks', 'regulatoryHurdles', i)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <Label>Key Risks & Mitigations</Label>
                <div className="space-y-4 border p-4 rounded-lg">
                  <div className="space-y-2">
                    <Input
                      placeholder="Risk description"
                      value={newRisk.risk}
                      onChange={e => setNewRisk({ ...newRisk, risk: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Select 
                      value={newRisk.impact}
                      onValueChange={(value: any) => setNewRisk({ ...newRisk, impact: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select impact level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low Impact</SelectItem>
                        <SelectItem value="medium">Medium Impact</SelectItem>
                        <SelectItem value="high">High Impact</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Mitigation strategy"
                      value={newRisk.mitigation}
                      onChange={e => setNewRisk({ ...newRisk, mitigation: e.target.value })}
                      className="min-h-[80px]"
                    />
                  </div>
                  <Button onClick={addRisk} className="w-full">
                    Add Risk
                  </Button>
                </div>

                <div className="space-y-2 mt-4">
                  {form.risks.keyRisks.map((risk, i) => (
                    <div key={i} className="flex items-center justify-between bg-secondary p-3 rounded-lg">
                      <div>
                        <p className="font-medium">{risk.risk}</p>
                        <p className="text-sm text-muted-foreground">Impact: {risk.impact}</p>
                        <p className="text-sm">Mitigation: {risk.mitigation}</p>
                      </div>
                      <X 
                        className="h-4 w-4 cursor-pointer" 
                        onClick={() => {
                          const newRisks = form.risks.keyRisks.filter((_, idx) => idx !== i);
                          setForm({ ...form, risks: { ...form.risks, keyRisks: newRisks } });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('ip')}>← Previous</Button>
                <Button onClick={() => setActiveTab('review')}>Next: Review →</Button>
              </div>
            </TabsContent>

            {/* Tab 8: Review & Submit */}
            <TabsContent value="review" className="space-y-6">
              <h3 className="text-lg font-semibold">Review Your Pitch</h3>
              <p className="text-sm text-muted-foreground">
                Please review all information before submitting. Our AI will analyze every section.
              </p>

              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-800">✓ Complete</h4>
                  <p className="text-sm text-green-700">
                    All required sections filled. Ready for analysis.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Company Name:</Label>
                  <p className="text-sm">{form.companyName}</p>
                </div>

                <div className="space-y-2">
                  <Label>Tagline:</Label>
                  <p className="text-sm">{form.tagline}</p>
                </div>

                <div className="space-y-2">
                  <Label>Traction Summary:</Label>
                  <p className="text-sm">
                    {form.traction.customerInterviews} customer interviews, 
                    {form.traction.waitlistSignups} waitlist signups,
                    MVP: {form.traction.mvpStatus}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Funding Ask:</Label>
                  <p className="text-sm">${form.financial.fundingAsk.toLocaleString()}</p>
                </div>

                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertDescription>
                    By submitting, you confirm that all information is accurate to the best of your knowledge.
                  </AlertDescription>
                </Alert>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('risks')}>← Previous</Button>
                <Button onClick={handleSubmit} size="lg" className="bg-green-600 hover:bg-green-700" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Pitch for Analysis'
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}